import React from 'react';
import { Link } from 'react-router-dom';
import { isEmpty, filter, orderBy } from 'lodash';
import { getInternalBaseUrl, openPreviewWindow } from '../../common/global.js';
import AddIcon from '@material-ui/icons/Add';
import { FormatListBulleted } from '@material-ui/icons';
import SendIcon from '@material-ui/icons/Send';
import { Visibility } from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Stops from './Stops';
import ACButtonIcon from '../../atoms/ACButtonIcon';
import ACTicketsDialog from '../../atoms/ACTicketsDialog';
import { fetchBookingTickets } from '../../common/actions/bookings';
import { getCostBearingStop, hasTicketDocuments } from 'utils/commonFunction';

import styles from './BookingDetails.module.scss';
import ACButton from 'components/atoms/ACButton.jsx';
import moment from 'moment-timezone';
import Checkbox from '@material-ui/core/Checkbox';
import { LocationOn, MenuBook } from '@material-ui/icons';

class BookingDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showVirtual: false,
      showRoute: false,
      openTicketPreviewDialog: false,
      ticketPreviewDocuments: [],
      isPersonalisationDone: false,
    };
    this.onClickSetRoute = this.onClickSetRoute.bind(this);
  }

  componentDidMount() {
    const { booking = {} } = this.props;
    const { route = {}, virtual = {} } = booking;

    this.setState({
      isPersonalisationDone:booking?.isPersonalisationDone
    })

    if (booking?.product?.category === 'digital' || booking?.product?.category === 'shipping') {
      this.setState({
        showRoute: false,
        showVirtual: true,
        stopsList: virtual?.stops || [],
        name: virtual?.name
      });
    } else if (booking?.product?.category === 'external') {
      this.setState({
        showRoute: true,
        showVirtual: false,
        stopsList: route?.stops || [],
        name: route?.name
      });
    } else {
      this.setState({
        showRoute: true,
        showVirtual: true,
        stopsList: route?.stops || virtual?.stops || [],
        name: route?.name
      });
    }
  }

  // stopsList is built from the booking on mount, so without this a route (or a
  // ticket route) attached while the row is already open shows no stops until reload.
  componentDidUpdate(prevProps) {
    const previousStops = prevProps.booking?.route?.stops || prevProps.booking?.virtual?.stops;
    const currentStops = this.props.booking?.route?.stops || this.props.booking?.virtual?.stops;

    if (previousStops !== currentStops) {
      this.setState({ stopsList: currentStops || [] });
    }
  }

  onClickSetRoute() {
    this.props.onClickSetRouteFunc();
  }

  getCostBearingStop() {
    return getCostBearingStop(this.props.booking || {});
  }

  onClickSetVirtual = () => {
    this.props.onClickSetVirtualFunc();
  };

  onClickAddBookingStop = () => {
    this.props.onClickAddBookingStopFunc();
  };

  onClickAddBookingStopSpark = () => {
    this.props.onClickAddBookingStopSparkFunc();
  };

  onClickPreviewBooking = async () => {
    const { booking } = this.props;

    // A generated one-stop route has no gameplay for eee to render, so preview the tickets
    // themselves. A real route does, even when some of its stops are ticketed, so it still
    // launches eee and its tickets are read from each stop's own dialog.
    if (getCostBearingStop(booking)) {
      const tickets = await fetchBookingTickets(booking.id, booking.occasionBookingId);
      const documents = (tickets?.stops || []).reduce(
        (accumulator, stop) => accumulator.concat(stop.documents || []),
        []
      );
      this.setState({ openTicketPreviewDialog: true, ticketPreviewDocuments: documents });
      return;
    }

    openPreviewWindow({ bookingId: booking.id });
  }

  onCloseTicketPreview = () => {
    this.setState({ openTicketPreviewDialog: false });
  };

  /**
   * Which of the component's allowed provider items delivers this booking.
   *
   * A partner component allows a set - several balloon operators, each with a standard, deluxe
   * and budget option - and aex seeds its one stop empty rather than guessing. Choosing here
   * fills in the provider, the item and the item's own price, so the cost is right without
   * anyone typing it, and any ticket requirement follows the item chosen.
   *
   * Absent unless the component published a list, so every other kind of booking is unchanged.
   */
  renderProviderItemChoice() {
    const { booking = {} } = this.props;
    const allowed = booking.allowedProviderItems || [];
    if (isEmpty(allowed)) return null;

    const chosenItemId = this.getCostBearingStop()?.itemId;
    const chosenId = chosenItemId ? String(chosenItemId) : '';
    const isChosen = allowed.some((link) => String(link.itemId) === chosenId);

    return (
      <span key="provider-item-choice" style={{ marginLeft: '10px', marginTop: '4px' }}>
        <TextField
          select
          size="small"
          value={isChosen ? chosenId : ''}
          onChange={(event) => this.props.onChooseComponentProviderItemFunc(event.target.value)}
          SelectProps={{ displayEmpty: true, getcontentanchorel: null }}
          style={{ minWidth: '230px' }}
          helperText={isChosen ? undefined : 'Not yet assigned - cost stays 0 until it is'}
        >
          <MenuItem value="" disabled>
            Choose a provider item
          </MenuItem>
          {allowed.map((link) => (
            <MenuItem key={link.itemId} value={String(link.itemId)}>
              {[link.providerName, link.itemName].filter(Boolean).join(' - ') || link.itemId}
              {link.price != null ? ` (${link.price})` : ''}
              {link.ticketRequired ? ' - ticket' : ''}
            </MenuItem>
          ))}
        </TextField>
      </span>
    );
  }

  /**
   * A self-serve component is done the moment it exists: the customer does it themselves from
   * content already on the component, so there is no route, no virtual and no ticket to wait
   * for. Saying so is the whole of the planner's job here - there is nothing to set.
   */
  renderSelfServe() {
    const { booking = {} } = this.props;
    if (!booking.selfServe) return null;

    return (
      <span
        key="self-serve"
        style={{ marginLeft: '10px', fontSize: '13px', marginTop: '7px', color: '#4caf50' }}
      >
        <b>Self-serve</b> - nothing to book, counted as delivered
      </span>
    );
  }

  renderTicketRequirement() {
    const { booking = {} } = this.props;
    const requirement = booking.ticketRequired;
    if (!requirement) return null;

    // Any stop's file counts: the ticket is attached once the customer has something to
    // present, wherever on the route it hangs.
    const isAttached = hasTicketDocuments(booking);

    return (
      <span
        key="ticket-requirement"
        style={{ marginLeft: '10px', fontSize: '13px', marginTop: '7px' }}
      >
        <b>Ticket required: </b>
        {requirement.bookingUrl ? (
          <a href={requirement.bookingUrl} target="_blank" rel="noopener noreferrer">
            {requirement.providerName}
          </a>
        ) : (
          requirement.providerName
        )}
        {!isAttached && <span style={{ color: '#ff765f' }}> — not attached</span>}
      </span>
    );
  }

  renderTicketPreviewDialog() {
    const ticketStop = this.getCostBearingStop();

    return (
      <ACTicketsDialog
        title="Customer Tickets"
        open={this.state.openTicketPreviewDialog}
        ticket={ticketStop?.ticket}
        stop={ticketStop}
        isPreview={true}
        documents={this.state.ticketPreviewDocuments}
        onClickCancelFunc={this.onCloseTicketPreview}
      />
    );
  }

  renderSetRoute() {
    return (
      <Tooltip title={<span style={{ fontSize: "13px" }}>Set Route</span>} placement='top' key="set-route">
        <div className={styles.verticalAlignedFlex}>
          <ACButtonIcon
            id={this.props.booking.id}
            text={''}
            icon={<AddIcon key="circle-icon" />}
            onClickFunc={() => this.onClickSetRoute()}
          />
        </div>
      </Tooltip>
    );
  }

  renderSetVirtual() {
    return (
      <Tooltip title={<span style={{ fontSize: "13px" }}>Set Virtual Experience</span>} placement='top' key="set-virtual">
        <div>
          <ACButtonIcon
            id={this.props.booking.id}
            text={''}
            icon={<AddIcon key="circle-icon" />}
            onClickFunc={this.onClickSetVirtual}
            className={styles.routeActionButton}
          />
        </div>
      </Tooltip>
    );
  }

  renderPreviewButton() {
    return (
      <Tooltip title={<span style={{ fontSize: "13px" }}>Preview Booking</span>} placement='top' key="preview-booking">
        <div className={styles.verticalAlignedFlex}>
          <ACButtonIcon
            id={this.props.booking.id}
            text={''}
            icon={<Visibility style={{ margin: 'auto', color: '#78828a' }} />}
            margin="0px 0px 0px 7px"
            onClickFunc={() => this.onClickPreviewBooking()}
            className={styles.routeActionButton}
          />
        </div>
      </Tooltip>

    )
  }

  renderLeft() {
    const { booking = {} } = this.props;
    const { route = {}, virtual = {} } = booking;
    const isBookingAssigned = route?.name || virtual?.name;

    let setComponents = [];

    // A ticketed component used to be given only the ticket button — no route to set, no
    // stops to add, nothing to map. That was right while a ticket replaced a component's
    // whole route, and is backwards now that a ticket belongs to a stop: a ticketed
    // component has to be able to carry a real multi-stop route so that only the stops that
    // need one get a ticket. The requirement itself still shows, as context for the planner.
    if (this.state.showVirtual) {
      setComponents.push(this.renderSetVirtual());
    }
    if (this.state.showRoute) {
      setComponents.push(this.renderSetRoute());
    }
    setComponents.push(this.renderSelfServe());
    setComponents.push(this.renderProviderItemChoice());
    setComponents.push(this.renderTicketRequirement());

    // One preview button. Each branch used to push its own — the virtual one here and a
    // copy of it inside `renderRouteRelatedActions` — so the eye rendered twice on any
    // booking whose product category left both flags set. That is every occasion
    // component: a component's product is built name-only and carries no `category`.
    isBookingAssigned && setComponents.push(this.renderPreviewButton());

    // Name only what is actually attached. A route-backed booking used to get the virtual
    // "Assigned:" label as well, with an empty name and a link to `virtuals?id=undefined`.
    this.state.showVirtual && virtual?.name && setComponents.push(this.renderVirtualRelatedActions());
    this.state.showRoute && route?.name && setComponents.push(this.renderRouteRelatedActions());

    return setComponents;
  }

  renderVirtualRelatedActions() {
    const { virtual = {}, virtualId } = this.props.booking;
    const { name = '' } = virtual;

    let gotToVirtualUrl = getInternalBaseUrl().virtuals + '?id=' + virtualId;


    return (
      <>
        <span style={{ marginLeft: '10px', fontSize: '13px', marginTop: '7px' }}>
          <Link
            to={gotToVirtualUrl}
            target="_blank"
            style={{ textDecoration: 'underline', color: 'inherit', border: '0px dashed #bbb', padding: '8px' }}
          >

            <b>Assigned: </b>  {name}
          </Link>

        </span>
      </>
    );
  }

  renderRouteRelatedActions() {
    let { googleMapURL = '' } = this.props;
    const { route = {}, routeId, productId } = this.props.booking;
    const { name = '', stops = [] } = route;



    let goToRouteURL = getInternalBaseUrl().routes + '?id=' + routeId + '&productId=' + productId;

    let mapURL = `${googleMapURL}/maps/dir/`;
    // sometimes stops can be reset to an empty object, so we need to check for that
    const isStopsCorrupt = !Array.isArray(stops);
    if (!isStopsCorrupt) {
      stops.forEach((stop) => {
        const { provider = {} } = stop;
        const { where = {} } = provider;
        const { address = '' } = where;
        if (stop.providerId && stop.type !== 'payment-only') {
          mapURL = mapURL.concat(address, '/');
        }
      });
    } else {
      return (
        <div className={styles.verticalAlignedFlex}>
          This route appears to be corrupt.
        </div>
      )
    }

    return (
      <>
        {' '}
        <Tooltip title={<span style={{ fontSize: "13px" }}>Add Stop</span>} placement='top'>
          <div className={styles.verticalAlignedFlex} key="add-stop">
            <ACButtonIcon
              id={this.props.booking.id}
              text={''}
              icon={<LocationOn key="circle-icon" />}
              margin="0px 0px 0px 7px"
              onClickFunc={() => this.onClickAddBookingStop()}
              className={styles.routeActionButton}
            />
          </div>
        </Tooltip>
        <Tooltip title={<span style={{ fontSize: "13px" }}>Add Spark</span>} placement='top'>
          <div className={styles.verticalAlignedFlex} key="add-spark">
            <ACButtonIcon
              id={this.props.booking.id}
              text={''}
              icon={<MenuBook key="circle-icon" />}
              margin="0px 0px 0px 7px"
              onClickFunc={() => this.onClickAddBookingStopSpark()}
              className={styles.routeActionButton}
            />
          </div>
        </Tooltip>
        <Tooltip title={<span style={{ fontSize: "13px" }}>Google Maps</span>} placement='top'>
          <div className={styles.verticalAlignedFlex} key="google-maps">
            <a
              href={mapURL}
              target="_blank"
              className={styles.previewRouteInGoogleMap}
              rel="noopener noreferrer"
            >
              <FormatListBulleted style={{ margin: 'auto', color: '#78828a' }} />
            </a>
          </div>
        </Tooltip>
        {/*
          A generated route — the one-stop route a partner or ticketed component is seeded
          with at HQ sync — has no library record behind it, so `routeId` is undefined and
          this link would point at the routes page for nothing. Name it without linking.
        */}
        <span style={{ marginLeft: '10px', fontSize: '13px', marginTop: '7px' }}>
          {routeId ? (
            <Link
              to={goToRouteURL}
              target="_blank"
              style={{ textDecoration: 'underline', color: 'inherit', border: '0px dashed #bbb', padding: '8px' }}
            >
              <b>Assigned: </b>  {name}
            </Link>
          ) : (
            <span style={{ padding: '8px' }}>
              <b>Assigned: </b>  {name}
            </span>
          )}
        </span>
      </>
    );
  }

  handleWpChange = (event) => {
    this.setState({ isPersonalisationDone: event.target.checked });
    this.props.onUpdatePersonalisationCheckFunc(event.target.checked);
  }

  renderTop() {
    const { booking = {} } = this.props;
    const { showPublishBtn, lastPublishedAt } = booking;
    let publishColor = '#434F69';
    let publishButtonBorder = publishColor + ' 1px solid';
    let publishButtonBackgroundColor = '';
    let publishButtonColor = publishColor;
    return (
      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
        <div className={styles.leftRightFlex}>{this.renderLeft()}</div>
        <div className={styles.rightFlex}>
            {!isEmpty(this.state.stopsList) && (<div>
              <span>WP</span>
              <Checkbox checked={this.state?.isPersonalisationDone} onChange={this.handleWpChange}  style={{color:this.state?.isPersonalisationDone?'#66CDAA':''}} />
            </div>)}
          {showPublishBtn && (
            <ACButton
              text="Publish Changes"
              iconLast={false}
              onClickFunc={this.props.onPublishBookingFunc}
              border={publishButtonBorder}
              backgroundColor={publishButtonBackgroundColor}
              color={publishButtonColor}
              margin="5px 5px 5px 5px"
              padding="5px 15px"
              icon={<SendIcon key="send-icon" />}
              className={styles.sendNotificationButtonStyle}
            />
          )}
          {!showPublishBtn && lastPublishedAt && (
            <p style={{ color: publishButtonColor, margin: '5px', padding: '5px 15px' }}>Last published at: {moment(lastPublishedAt).format('DD/MM/YYYY hh:mma')}</p>
          )}
        </div>
      </div>
    );
  }

  handleStopsSwap = (data) => {
    this.props.onSwapStopsFunc(data,this.props.booking);
  }

  render() {
    const { booking = {} } = this.props;

    let virtualExpInfoText = '';
    if (!isEmpty(booking?.virtual)) {
      virtualExpInfoText = isEmpty(booking?.virtual.stops)
        ? `Virtual experience (${this.state.name}) has been added. This experience does not have any EEE stops` : '';

    }
    return (
      <div className={styles.fullWidthColumn}>
        {this.renderTop()}
        <Stops
          stops={this.state.stopsList}
          notActiveItems={this.props.notActiveItems}
          onUpdateBookingItemPaymentFunc={this.props.onUpdateBookingItemPaymentFunc}
          onUpdateBookingItemActivityFunc={this.props.onUpdateBookingItemActivityFunc}
          onUpdateStopBookingNoteFunc={this.props.onUpdateStopBookingNoteFunc}
          onUpdateBookingItemPriceFunc={this.props.onUpdateBookingItemPriceFunc}
          onSelectStopFunc={this.props.onSelectStopFunc}
          onSwapStopsFunc={this.handleStopsSwap}
          handleCorruptStops={this.props.handleCorruptStops}
          bookingId={this.props.booking.id}
          occasionBookingId={this.props.booking.occasionBookingId}
        />
        {virtualExpInfoText && <div>{virtualExpInfoText}</div>}
        {this.renderTicketPreviewDialog()}
      </div>
    );
  }

  isRouteChanged = (alerts = []) => {
    let aexhqRouteAlerts = false;

    const sortedAlerts = orderBy(alerts, ['dateTime'], ['desc']);

    if (!isEmpty(sortedAlerts)) {
      aexhqRouteAlerts = filter(sortedAlerts, (a) => {
        return a.type && a.type === 'aex-hq-route';
      });
    }

    return aexhqRouteAlerts;
  }

}

// No `connect` wrapper: its only bindings were the provider list and search that the
// component-level ticket dialog used. A stop's provider and cost are edited on the stop row
// itself, so nothing here reads the store any more.
export default BookingDetails;
